import { useState, useMemo } from 'react';
import {
  SlidersHorizontal, Plus, Trash2, ToggleLeft, ToggleRight, ChevronDown,
  ChevronRight, LayoutGrid, Pill, List, Circle, AlignLeft,
  Hash, Calendar, ToggleLeft as ToggleIcon, Link2, X, BarChart2,
} from 'lucide-react';
import { PageHeader } from '@/components/prioritization/PageHeader';
import type { Attribute, CustomFieldDisplayType } from '@/types/prioritization';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { useUIStore } from '@/stores/prioritization/uiStore';
import { useCanDefineAttributes } from '@/hooks/prioritization/useCanDefineAttributes';
import { placementSummary } from '@/lib/customFieldPlacement';
import { CustomFieldPlacementControl } from './CustomFieldPlacementControl';

// ── Object type options ────────────────────────────────────────────────────────

const OBJECT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'item',         label: 'Items' },
  { value: 'roadmap_node', label: 'Roadmap Items' },
];

function objectTypeLabel(value?: string): string {
  return OBJECT_TYPE_OPTIONS.find((o) => o.value === (value ?? 'item'))?.label ?? 'Items';
}

// ── Display type catalogue ─────────────────────────────────────────────────────

interface DisplayTypeOption {
  value: CustomFieldDisplayType;
  label: string;
  description: string;
  icon: React.ReactNode;
  /** Which field types this display type supports */
  forTypes: Attribute['type'][];
}

const DISPLAY_TYPE_OPTIONS: DisplayTypeOption[] = [
  {
    value: 'cards',
    label: 'Cards',
    description: 'Each option is a tappable card — like altitude levels. Best for 3–8 choices.',
    icon: <LayoutGrid className="h-4 w-4" />,
    forTypes: ['select', 'multiselect'],
  },
  {
    value: 'pills',
    label: 'Pills',
    description: 'Compact chip buttons in a row. Great for multi-select with many options.',
    icon: <Pill className="h-4 w-4" />,
    forTypes: ['select', 'multiselect'],
  },
  {
    value: 'dropdown',
    label: 'Dropdown',
    description: 'Standard select dropdown. Good when there are many choices.',
    icon: <List className="h-4 w-4" />,
    forTypes: ['select', 'multiselect'],
  },
  {
    value: 'radio',
    label: 'Radio buttons',
    description: 'Horizontal radio buttons. Best for 2–5 options.',
    icon: <Circle className="h-4 w-4" />,
    forTypes: ['select'],
  },
  {
    value: 'toggle',
    label: 'Toggle',
    description: 'Yes / No switch.',
    icon: <ToggleIcon className="h-4 w-4" />,
    forTypes: ['boolean'],
  },
  {
    value: 'text-input',
    label: 'Text input',
    description: 'Free-form single-line text.',
    icon: <AlignLeft className="h-4 w-4" />,
    forTypes: ['text'],
  },
  {
    value: 'number-input',
    label: 'Number input',
    description: 'Numeric field with optional min / max validation.',
    icon: <Hash className="h-4 w-4" />,
    forTypes: ['number'],
  },
  {
    value: 'date-picker',
    label: 'Date picker',
    description: 'Calendar date picker.',
    icon: <Calendar className="h-4 w-4" />,
    forTypes: ['date'],
  },
];

function displayTypesForFieldType(type: Attribute['type']): DisplayTypeOption[] {
  return DISPLAY_TYPE_OPTIONS.filter((d) => d.forTypes.includes(type));
}

function defaultDisplayType(type: Attribute['type']): CustomFieldDisplayType | undefined {
  switch (type) {
    case 'select':      return 'dropdown';
    case 'multiselect': return 'pills';
    case 'boolean':     return 'toggle';
    case 'text':        return 'text-input';
    case 'number':      return 'number-input';
    case 'date':        return 'date-picker';
    default:            return undefined;
  }
}

// ── Blank new-field state ──────────────────────────────────────────────────────

function blankField(): Partial<Attribute> {
  return {
    type: 'text',
    required: false,
    isCustomField: true,
    enabled: true,
    objectType: 'item',
    customDisplayType: 'text-input',
  };
}

// ── Conditional-options editor ─────────────────────────────────────────────────

function ConditionalOptionsEditor({
  parentOptions,
  conditionalOptions,
  onChange,
}: {
  parentOptions: string[];
  conditionalOptions: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>(
    Object.fromEntries(parentOptions.map((p) => [p, '']))
  );

  const addOption = (parentVal: string) => {
    const raw = (drafts[parentVal] ?? '').trim();
    if (!raw) return;
    const existing = conditionalOptions[parentVal] ?? [];
    if (existing.includes(raw)) return;
    onChange({ ...conditionalOptions, [parentVal]: [...existing, raw] });
    setDrafts((d) => ({ ...d, [parentVal]: '' }));
  };

  const removeOption = (parentVal: string, opt: string) => {
    const next = (conditionalOptions[parentVal] ?? []).filter((o) => o !== opt);
    onChange({ ...conditionalOptions, [parentVal]: next });
  };

  return (
    <div className="space-y-4">
      {parentOptions.map((parentVal) => {
        const opts = conditionalOptions[parentVal] ?? [];
        return (
          <div key={parentVal} className="border border-[var(--rule)] bg-[var(--surface)] p-3">
            <p className="text-xs font-semibold text-[var(--text-sec)] mb-2 flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded bg-[var(--slate-dim)] text-[var(--text-sec)]">{parentVal}</span>
              <span className="text-[var(--text-ter)] font-normal">→ options shown when parent equals this</span>
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
              {opts.length === 0 && (
                <span className="text-xs text-[var(--text-ter)] italic">No options yet</span>
              )}
              {opts.map((opt) => (
                <span key={opt} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--bg-raised)] border border-[var(--rule)] text-[var(--text-sec)]">
                  {opt}
                  <button type="button" onClick={() => removeOption(parentVal, opt)} className="text-[var(--text-ter)] hover:text-[var(--coral)]">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={drafts[parentVal] ?? ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [parentVal]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(parentVal); } }}
                placeholder="Add option…"
                className="flex-1 text-xs px-2 py-1 border border-[var(--rule)] focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={() => addOption(parentVal)}
                className="text-xs px-2.5 py-1 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
              >
                Add
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Add / Edit form ────────────────────────────────────────────────────────────

function FieldForm({
  field,
  allCustomFields,
  onChange,
  onSave,
  onCancel,
  saveLabel = 'Add Field',
}: {
  field: Partial<Attribute>;
  allCustomFields: Attribute[];
  onChange: (updates: Partial<Attribute>) => void;
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
}) {
  const [optionDraft, setOptionDraft] = useState('');
  const displayTypeOptions = displayTypesForFieldType(field.type ?? 'text');

  const addOption = () => {
    const raw = optionDraft.trim();
    if (!raw) return;
    const existing = field.options ?? [];
    if (existing.includes(raw)) return;
    onChange({ options: [...existing, raw] });
    setOptionDraft('');
  };

  const removeOption = (opt: string) => {
    onChange({ options: (field.options ?? []).filter((o) => o !== opt) });
  };

  // Eligible parent fields: select-type custom fields that are NOT this field
  const parentCandidates = allCustomFields.filter(
    (f) => f.type === 'select' && f.key !== field.key && f.objectType === (field.objectType ?? 'item')
  );

  const selectedParent = field.parentKey
    ? allCustomFields.find((f) => f.key === field.parentKey)
    : undefined;

  const hasOptions = field.type === 'select' || field.type === 'multiselect';

  return (
    <div className="space-y-5">
      {/* Row 1: key / label / object type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Key (Internal) *</label>
          <input
            type="text"
            value={field.key || ''}
            onChange={(e) => onChange({ key: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
            placeholder="e.g., division"
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Label (Display) *</label>
          <input
            type="text"
            value={field.label || ''}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="e.g., Division"
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Applies To</label>
          <div className="relative">
            <select
              value={field.objectType ?? 'item'}
              onChange={(e) => onChange({ objectType: e.target.value, parentKey: undefined, conditionalOptions: undefined })}
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm appearance-none"
            >
              {OBJECT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
          </div>
        </div>
      </div>

      {/* Row 2: field type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Field Type</label>
          <div className="relative">
            <select
              value={field.type || 'text'}
              onChange={(e) => {
                const t = e.target.value as Attribute['type'];
                onChange({
                  type: t,
                  options: undefined,
                  customDisplayType: defaultDisplayType(t),
                  parentKey: t !== 'select' ? undefined : field.parentKey,
                  conditionalOptions: t !== 'select' ? undefined : field.conditionalOptions,
                });
              }}
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm appearance-none"
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
              <option value="boolean">Yes / No</option>
              <option value="select">Single Select</option>
              <option value="multiselect">Multi-Select</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Help Text <span className="font-normal text-[var(--text-ter)]">(optional)</span></label>
          <input
            type="text"
            value={field.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Short hint shown on the field"
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
          />
        </div>
      </div>

      {/* Display type selector */}
      {displayTypeOptions.length > 1 && (
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">UI Representation</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {displayTypeOptions.map((dt) => {
              const active = (field.customDisplayType ?? defaultDisplayType(field.type ?? 'text')) === dt.value;
              return (
                <button
                  key={dt.value}
                  type="button"
                  onClick={() => onChange({ customDisplayType: dt.value })}
                  className={`flex flex-col items-start gap-1.5 p-3  border text-left transition-colors ${
                    active
                      ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:border-[var(--rule)]'
                  }`}
                >
                  <span className={active ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}>{dt.icon}</span>
                  <span className="text-xs font-semibold">{dt.label}</span>
                  <span className="text-xs text-[var(--text-ter)] leading-tight">{dt.description}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Options editor (for select/multiselect without a parent) */}
      {hasOptions && !field.parentKey && (
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Options</label>
          <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
            {(field.options ?? []).length === 0 && (
              <span className="text-xs text-[var(--text-ter)] italic">No options yet — add below</span>
            )}
            {(field.options ?? []).map((opt) => (
              <span key={opt} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)]">
                {opt}
                <button type="button" onClick={() => removeOption(opt)} className="text-[var(--text-sec)] hover:text-[var(--coral)]">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={optionDraft}
              onChange={(e) => setOptionDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
              placeholder="Type an option and press Enter or Add"
              className="flex-1 px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button type="button" onClick={addOption} className="px-3 py-2 bg-[var(--slate)] text-white text-sm hover:bg-[var(--slate)]">
              Add
            </button>
          </div>
        </div>
      )}

      {/* Parent field (hierarchy) */}
      {field.type === 'select' && parentCandidates.length > 0 && (
        <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="h-4 w-4 text-[var(--text-ter)]" />
            <span className="text-sm font-semibold text-[var(--text-sec)]">Parent Field (Hierarchy)</span>
          </div>
          <p className="text-xs text-[var(--text-sec)] mb-3">
            Make this a child field — it will only appear after the parent has a value, and its options will
            vary depending on what the parent is set to (e.g. Division → Team).
          </p>
          <div className="relative mb-4">
            <select
              value={field.parentKey ?? ''}
              onChange={(e) => {
                const pk = e.target.value || undefined;
                onChange({ parentKey: pk, conditionalOptions: pk ? (field.conditionalOptions ?? {}) : undefined });
              }}
              className="w-full px-3 py-2 border border-[var(--slate)] text-sm appearance-none bg-[var(--bg-raised)]"
            >
              <option value="">— No parent (standalone field) —</option>
              {parentCandidates.map((f) => (
                <option key={f.key} value={f.key}>{f.label} ({f.key})</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
          </div>
          {field.parentKey && selectedParent && (
            <>
              <p className="text-xs font-medium text-[var(--text-sec)] mb-2">
                Define which options appear for each value of <strong>{selectedParent.label}</strong>:
              </p>
              <ConditionalOptionsEditor
                parentOptions={selectedParent.options ?? []}
                conditionalOptions={field.conditionalOptions ?? {}}
                onChange={(next) => onChange({ conditionalOptions: next })}
              />
            </>
          )}
          {field.parentKey && selectedParent && (selectedParent.options ?? []).length === 0 && (
            <p className="text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] p-2 mt-2">
              The parent field "{selectedParent.label}" has no options yet. Add options to it first, then come back to define the child options.
            </p>
          )}
        </div>
      )}

      {/* Where it shows (item fields) */}
      <CustomFieldPlacementControl
        field={field}
        parentLabel={selectedParent?.label}
        onChange={(placement) => onChange({ placement })}
      />

      {/* Scoring criterion opt-in */}
      <div className="border border-[var(--rule)] bg-[var(--surface)] dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] p-4">
        <div className="flex items-start gap-3">
          <BarChart2 className="h-4 w-4 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Use as scoring criterion</p>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                  When on, this field appears as an option in criteria pickers (tracks, sessions, workflows, templates).
                  Only enable this for numeric or scored fields where participants will assign values during prioritization.
                </p>
              </div>
              <button
                type="button"
                onClick={() => onChange({ usable_as_criterion: !field.usable_as_criterion })}
                title={field.usable_as_criterion ? 'Remove from criteria pickers' : 'Allow use as scoring criterion'}
                className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
                  field.usable_as_criterion ? 'bg-[var(--slate)]' : 'bg-[var(--rule-bright)]'
                }`}
              >
                <span className={`block w-3.5 h-3.5 bg-[var(--bg-raised)] rounded-full shadow transition-transform mx-0.5 my-0.5 ${field.usable_as_criterion ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-1">
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
        <button
          type="button"
          onClick={onSave}
          className="btn btn-primary"
          disabled={!field.key || !field.label}
        >
          {saveLabel}
        </button>
      </div>
    </div>
  );
}

// ── Field list row ─────────────────────────────────────────────────────────────

const DISPLAY_TYPE_LABELS: Record<string, string> = {
  cards: 'Cards',
  pills: 'Pills',
  dropdown: 'Dropdown',
  radio: 'Radio',
  toggle: 'Toggle',
  'text-input': 'Text',
  'number-input': 'Number',
  'date-picker': 'Date',
};

function FieldRow({
  field,
  allCustomFields,
  canEdit,
  onToggle,
  onDelete,
  onUpdate,
}: {
  field: Attribute;
  allCustomFields: Attribute[];
  canEdit: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onUpdate: (updates: Partial<Attribute>) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isEnabled = field.enabled !== false;
  const parentField = field.parentKey ? allCustomFields.find((f) => f.key === field.parentKey) : undefined;
  const childFields = allCustomFields.filter((f) => f.parentKey === field.key);

  return (
    <div className={`${isEnabled ? '' : 'opacity-60'}`}>
      <div className="flex items-center gap-3 py-3">
        {/* Toggle */}
        {canEdit ? (
          <button
            type="button"
            onClick={onToggle}
            title={isEnabled ? 'Disable field' : 'Enable field'}
            className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
              isEnabled ? 'bg-[var(--slate)]' : 'bg-[var(--rule-bright)]'
            }`}
          >
            <span className={`block w-3.5 h-3.5 bg-[var(--bg-raised)] rounded-full shadow transition-transform mx-0.5 my-0.5 ${isEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
          </button>
        ) : isEnabled ? (
          <ToggleRight className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0" />
        ) : (
          <ToggleLeft className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0" />
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--text-pri)]">{field.label}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-ter)] font-mono">{field.key}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)] capitalize">{field.type}</span>
            {field.customDisplayType && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]">
                {DISPLAY_TYPE_LABELS[field.customDisplayType] ?? field.customDisplayType}
              </span>
            )}
            {parentField && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)] flex items-center gap-1">
                <Link2 className="h-3 w-3" /> child of {parentField.label}
              </span>
            )}
            {childFields.length > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
                {childFields.length} child field{childFields.length !== 1 ? 's' : ''}
              </span>
            )}
            {field.usable_as_criterion && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] flex items-center gap-1">
                <BarChart2 className="h-3 w-3" /> criterion
              </span>
            )}
          </div>
          {field.description && <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{field.description}</p>}
        </div>

        <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--surface)] text-[var(--text-ter)] border border-[var(--rule)] flex-shrink-0 whitespace-nowrap hidden sm:inline">
          {objectTypeLabel(field.objectType)}
        </span>
        {(field.objectType ?? 'item') === 'item' && (
          <span className="text-xs text-[var(--text-ter)] flex-shrink-0 whitespace-nowrap hidden md:inline" title="Shows on" data-testid="custom-field-shows-on-summary">
            {placementSummary(field, parentField?.label)}
          </span>
        )}

        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 hidden sm:inline ${
          isEnabled ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
        }`}>
          {isEnabled ? 'Enabled' : 'Disabled'}
        </span>

        {canEdit && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
              title="Edit field"
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
              title="Delete field"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {expanded && canEdit && (
        <div className="ml-12 pb-4 border-b border-[var(--rule)]">
          <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
            <FieldForm
              field={field}
              allCustomFields={allCustomFields}
              onChange={(updates) => onUpdate(updates)}
              onSave={() => setExpanded(false)}
              onCancel={() => setExpanded(false)}
              saveLabel="Save Changes"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function CustomFields() {
  const attributes = useAttributesStore((state) => state.attributes);
  const addAttribute = useAttributesStore((state) => state.addAttribute);
  const updateAttribute = useAttributesStore((state) => state.updateAttribute);
  const deleteAttribute = useAttributesStore((state) => state.deleteAttribute);
  const addNotification = useUIStore((state) => state.addNotification);

  const [isAdding, setIsAdding] = useState(false);
  const [newField, setNewField] = useState<Partial<Attribute>>(blankField());

  const canEdit = useCanDefineAttributes();

  const customFields = useMemo(() => attributes.filter((a) => a.isCustomField), [attributes]);
  const enabledCount = useMemo(() => customFields.filter((a) => a.enabled !== false).length, [customFields]);

  const handleAdd = async () => {
    if (!newField.key || !newField.label) {
      addNotification({ type: 'error', title: 'Missing Fields', message: 'Please provide both a key and label', duration: 3000 });
      return;
    }
    try {
      const field: Attribute = {
        key: newField.key.toLowerCase().replace(/\s+/g, '-'),
        label: newField.label,
        type: newField.type || 'text',
        required: newField.required || false,
        options: newField.options,
        description: newField.description,
        isCustomField: true,
        enabled: true,
        scope: 'global',
        inheritable: true,
        overridable: true,
        objectType: newField.objectType ?? 'item',
        customDisplayType: newField.customDisplayType ?? defaultDisplayType(newField.type ?? 'text'),
        parentKey: newField.parentKey,
        conditionalOptions: newField.conditionalOptions,
        placement: newField.placement,
      };
      const result = await addAttribute(field);
      if (!result?.error) {
        setNewField(blankField());
        setIsAdding(false);
        addNotification({ type: 'success', title: 'Custom Field Added', message: `"${field.label}" is now available`, duration: 3000 });
      }
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: (error instanceof Error && error.message) || 'Failed to add custom field', duration: 3000 });
    }
  };

  const handleUpdate = async (key: string, updates: Partial<Attribute>) => {
    await updateAttribute(key, updates);
  };

  const handleToggle = async (key: string, currentEnabled: boolean) => {
    await updateAttribute(key, { enabled: !currentEnabled });
  };

  const handleDelete = async (key: string) => {
    try {
      const result = await deleteAttribute(key);
      if (!result?.error) {
        addNotification({ type: 'success', title: 'Field Deleted', message: 'Custom field removed', duration: 3000 });
      }
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: (error instanceof Error && error.message) || 'Failed to delete field', duration: 3000 });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <PageHeader
          icon={<SlidersHorizontal className="h-4 w-4 text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)]"
          title="Custom Fields"
          subtitle="Define structured metadata fields for items and other objects. Set how each field is displayed, and link fields together in parent-child hierarchies."
        />
        {canEdit && (
          <button onClick={() => setIsAdding(true)} className="mt-1 btn btn-primary flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Field
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Total Fields</p>
          <p className="text-3xl font-bold text-[var(--text-pri)]">{customFields.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Enabled</p>
          <p className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{enabledCount}</p>
        </div>
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Disabled</p>
          <p className="text-3xl font-bold text-[var(--text-ter)]">{customFields.length - enabledCount}</p>
        </div>
      </div>

      {/* Add form */}
      {isAdding && canEdit && (
        <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
          <h3 className="font-semibold text-[var(--text-pri)] mb-4">New Custom Field</h3>
          <FieldForm
            field={newField}
            allCustomFields={customFields as Attribute[]}
            onChange={(updates) => setNewField((prev) => ({ ...prev, ...updates }))}
            onSave={handleAdd}
            onCancel={() => { setIsAdding(false); setNewField(blankField()); }}
            saveLabel="Add Field"
          />
        </div>
      )}

      {/* Fields list */}
      <div className="card">
        {customFields.length === 0 && !isAdding ? (
          <div className="text-center py-12 text-[var(--text-ter)]">
            <SlidersHorizontal className="h-9 w-9 mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">No custom fields yet</p>
            <p className="text-xs mt-1 max-w-xs mx-auto">
              Add fields like "Division", "Team", or "Launch Date" — choose the field type, display style,
              and optionally link fields in a parent-child hierarchy.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)]">
            {customFields.map((field) => (
              <FieldRow
                key={field.key}
                field={field}
                allCustomFields={customFields as Attribute[]}
                canEdit={canEdit}
                onToggle={() => handleToggle(field.key, field.enabled !== false)}
                onDelete={() => handleDelete(field.key)}
                onUpdate={(updates) => handleUpdate(field.key, updates)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Help card */}
      <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
        <h3 className="font-medium text-[var(--text-sec)] mb-3">About Custom Fields</h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm text-[var(--text-sec)]">
          <div>
            <p className="font-semibold mb-1">Field Types</p>
            <ul className="space-y-0.5 text-xs">
              <li><strong>Text</strong> — free-form single-line input</li>
              <li><strong>Number</strong> — integer or decimal</li>
              <li><strong>Date</strong> — calendar date</li>
              <li><strong>Yes / No</strong> — boolean toggle</li>
              <li><strong>Single Select</strong> — one choice from a list</li>
              <li><strong>Multi-Select</strong> — multiple choices</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold mb-1">UI Representations</p>
            <ul className="space-y-0.5 text-xs">
              <li><strong>Cards</strong> — each option is a distinct card (like altitude)</li>
              <li><strong>Pills</strong> — compact chip buttons</li>
              <li><strong>Dropdown</strong> — standard select menu</li>
              <li><strong>Radio</strong> — horizontal radio buttons (2–5 options)</li>
            </ul>
            <p className="font-semibold mb-1 mt-3">Hierarchies</p>
            <p className="text-xs">Link a child field to a parent (e.g. Division → Team). The child field shows options specific to the chosen parent value.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
